import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { useLocaleContext } from '../contexts/LocaleContext';
import { useLayoutContext } from '../contexts/LayoutContext';
import { getLocalizedLink } from './LocalizedLink';
import { ClearButton } from './input/ClearButton';
import { screenSize } from '../styles/ScreenSize';

const Wrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	flex: 0 1 360px;
	min-width: 0;
	margin: 0 24px;

	${screenSize.sm} {
		flex: 1;
		margin: 0 12px;
	}
`;

const IconButton = styled.button`
	display: flex;
	align-items: center;
	border: none;
	background: transparent;
	padding: 4px;
	margin-right: 12px;
	color: var(--secondary-color);
	cursor: pointer;

	&:hover {
		color: var(--hover-color);
	}

	svg {
		width: 18px;
		height: 18px;
	}
`;

const InputWrapper = styled.form`
	display: flex;
	align-items: center;
	width: 100%;
	height: 32px;
	margin: 0; // layout.css adds margin-bottom to all forms
	padding: 0 10px;
	background: #fff;
	border: 2px solid var(--secondary-color);
	border-radius: 4px;
	color: var(--secondary-color);

	> svg {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		margin-right: 8px;
	}
`;

const Input = styled.input`
	flex: 1;
	min-width: 0;
	height: 100%;
	border: none;
	outline: none;
	background: transparent;
	font: inherit;
	font-weight: 400;
	color: var(--primary-color);

	&::-webkit-search-cancel-button {
		display: none;
	}
`;

const Dropdown = styled.div`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	right: 0;
	background: #fff;
	border-radius: 4px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
	padding: 8px 0;
	font-weight: 400;

	// The search box is narrow on mobile, so the dropdown spans the screen under the header
	${screenSize.sm} {
		position: fixed;
		top: calc(var(--nav-bar-height) + 4px);
		left: var(--padding);
		right: var(--padding);
	}
`;

const SuggestionsTitle = styled.div`
	padding: 4px 12px 8px;
	font-size: 12px;
	font-weight: 600;
	color: var(--secondary-color);
`;

const Chips = styled.ul`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	list-style: none;
	margin: 0;
	padding: 0 12px 4px;

	li {
		max-width: 100%;
		margin: 0;
		padding: 0;

		&::before {
			display: none;
		}
	}
`;

const Chip = styled.button`
	max-width: 100%;
	overflow-wrap: anywhere; // only breaks a word wider than the whole dropdown
	text-align: left;
	border: 1px solid var(--grey);
	border-radius: 16px;
	background: #fff;
	padding: 4px 14px;
	font: inherit;
	font-size: 14px;
	font-weight: 400;
	color: var(--primary-color);
	cursor: pointer;

	&:hover {
		background: var(--light-grey);
	}
`;

const SearchIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
		<circle cx="10" cy="10" r="7" />
		<line x1="15.5" y1="15.5" x2="22" y2="22" strokeLinecap="round" />
	</svg>
);

const suggestionsQuery = graphql`
	query SearchSuggestions {
		allOtsingYaml {
			nodes {
				term
				parent {
					... on File {
						name
					}
				}
			}
		}
	}
`;

interface HeaderSearchProps {
	isOpen: boolean;
	setOpen: (open: boolean) => void;
}

export const HeaderSearch: React.FC<HeaderSearchProps> = ({ isOpen, setOpen }) => {
	const { t } = useTranslation();
	const { locale, defaultLocale } = useLocaleContext();
	const { setMenuOpen } = useLayoutContext();
	const [query, setQuery] = useState('');
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Suggestions come from content: otsing.<locale>.yaml
	const { allOtsingYaml } = useStaticQuery(suggestionsQuery);
	const suggestions: string[] = allOtsingYaml.nodes
		.filter((node: any) => node.parent?.name === `otsing.${locale}`)
		.map((node: any) => node.term)
		.filter(Boolean);

	function close() {
		setOpen(false);
		setQuery('');
	}

	// Leaving via search also closes the menu, so the new page isn't hidden behind it
	function goToResults(term: string) {
		const q = term.trim();
		if (!q) return;
		close();
		setMenuOpen(false);
		navigate(`${getLocalizedLink('/otsing', locale, defaultLocale)}?q=${encodeURIComponent(q)}`);
	}

	useEffect(() => {
		if (isOpen) inputRef.current?.focus();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		function handleClickOutside(e: MouseEvent) {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		goToResults(query);
	}

	if (!isOpen) {
		return (
			<IconButton aria-label={t('Otsing')} onClick={() => setOpen(true)}>
				<SearchIcon />
			</IconButton>
		);
	}

	const showSuggestions = !query.trim() && suggestions.length > 0;

	return (
		<Wrapper ref={wrapperRef}>
			<InputWrapper role="search" onSubmit={handleSubmit}>
				<SearchIcon />
				<Input
					ref={inputRef}
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => e.key === 'Escape' && close()}
					placeholder={t('Otsi...')}
					aria-label={t('Otsing')}
				/>
				{query && (
					<ClearButton
						onClick={() => {
							setQuery('');
							inputRef.current?.focus();
						}}
					/>
				)}
			</InputWrapper>
			{showSuggestions && (
				<Dropdown>
					<SuggestionsTitle>{t('Soovitused')}</SuggestionsTitle>
					<Chips>
						{suggestions.map((term) => (
							<li key={term}>
								<Chip type="button" onClick={() => goToResults(term)}>
									{term}
								</Chip>
							</li>
						))}
					</Chips>
				</Dropdown>
			)}
		</Wrapper>
	);
};
